import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import AuthShell from '../components/layout/AuthShell';
import AlertMessage from '../components/ui/AlertMessage';
import ResultIcon from '../components/ui/ResultIcon';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('checking'); // checking | success | error
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const started = useRef(false);

  // A megerősítés magától lefut a link megnyitásakor. A tokent csak ez a POST
  // használja el, így egy levelező-szkenner link-előtöltése nem égeti el.
  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setStatus('error');
      setMessage('This verification link is invalid or has expired.');
      return;
    }

    api.post('/auth/verify-email', { token })
      .then(res => {
        setStatus('success');
        setMessage(res.data.message || 'Your email address has been verified.');
      })
      .catch(err => {
        setStatus('error');
        setMessage(err.response?.data?.error || 'This verification link is invalid or has expired.');
      });
  }, [token]);

  if (status === 'checking') return <LoadingSpinner fullPage label="Verifying your email…" />;

  if (status === 'success') {
    return (
      <AuthShell title="Email verified" subtitle="Your account is ready">
        <ResultIcon tone="success" />
        <AlertMessage msg={{ type: 'success', text: message }} />
        <Link to="/login" className="btn-primary w-full py-3">Sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Email verification" subtitle="We could not verify this link">
      <ResultIcon tone="error" />
      <AlertMessage msg={{ type: 'error', text: message }} />

      {resendState === 'sent' ? (
        <>
          <AlertMessage msg={{
            type: 'success',
            text: 'If that email address needs verification, we have sent a new link to it.',
          }} />
          <Link to="/login" className="btn-primary w-full py-3">Back to sign in</Link>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            Verification links expire after 24 hours and can only be used once.
            Enter your email address and we'll send a new one.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setResendState('sending');
              try {
                await api.post('/auth/resend-verification', { email: resendEmail });
              } catch {
                /* a válasz szándékosan mindig azonos — hibát sem mutatunk */
              }
              setResendState('sent');
            }}
            className="space-y-5"
          >
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                placeholder="email@example.com"
                value={resendEmail}
                onChange={e => setResendEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <button type="submit" className="btn-primary w-full py-3" disabled={resendState === 'sending'}>
              {resendState === 'sending' ? 'Sending…' : 'Send new verification link'}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
